import React, { useEffect, useState } from 'react';
import {
  List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Checkbox, FormControlLabel,
  CircularProgress, Alert, Typography, Stack,
  Grid,
  FormHelperText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getTargetPeriods,
  addTargetPeriod,
  updateTargetPeriod,
  deleteTargetPeriod
} from './data';

export default function TargetPeriodsList() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [formValues, setFormValues] = useState({ id: '', period: '', active: false });
  const [saving, setSaving] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  const sanitizePeriods = (items = []) => {
    return (items ?? []).filter((period) => period && typeof period === 'object');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getTargetPeriods();
        // sort active first, then by name
        const { results } = data

        const sorted = sanitizePeriods(results).sort((a, b) => {
          if (a.active && !b.active) return -1;
          if (!a.active && b.active) return 1;
          return (a.name ?? '').localeCompare((b.name ?? ''), undefined, { sensitivity: 'base' });
        });
        setPeriods(sorted);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenAdd = () => {
    setEditingPeriod(null);
    setFormValues({ period: '', active: true });
    setOpenDialog(true);
  };

  const handleOpenEdit = (period) => {
    setEditingPeriod(period);
    setFormValues(period);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingPeriod) {
        const resp = await updateTargetPeriod(formValues);
        if (resp.status === 'success' && resp.instance) {
          setPeriods((prevPeriods) =>
            sanitizePeriods(prevPeriods).map((p) => (p.id === resp.instance.id ? resp.instance : p))
          );
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to save period: response did not include saved period data.');
        } else {
          setError(resp.error);
        }
      } else {
        const resp = await addTargetPeriod(formValues);
        if (resp.status === 'success' && resp.instance) {
          setPeriods((prevPeriods) => [...sanitizePeriods(prevPeriods), resp.instance]);
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to add period: response did not include saved period data.');
        } else {
          setError(resp.error);
        }
      }
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
    }
  };

  const confirmDeletePeriod = (period) => {
    setPeriodToDelete(period);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!periodToDelete) return;
    setSaving(true);
    setError('');
    try {
      const resp = await deleteTargetPeriod(periodToDelete.id);
      if (resp.status === 'success') {
        setPeriods((prevPeriods) => sanitizePeriods(prevPeriods).filter((p) => p.id !== periodToDelete.id));
      } else setError(resp.error);
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
      setConfirmDeleteOpen(false);
      setPeriodToDelete(null);
    }
  };

  if (loading) return <CircularProgress size="small" />;

  return (
    <>
    {(error !== '' && !openDialog && !confirmDeleteOpen) && <Alert severity="error">{error}</Alert>}
    <Grid container direction='row-reverse' p={1} justifyContent='space-between' maxWidth={600}>
      <Grid item xs={3} textAlign={'center'}>
        <Button variant="contained" size="small" onClick={handleOpenAdd}>Add Period</Button>
      </Grid>
      <Grid item xs={9}>
        <List dense disablePadding >
          {sanitizePeriods(periods).map(period => (
            <ListItem
              key={period.id}
              sx={{
                bgcolor: period.active ? 'background.paper' : '#fafafa'
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      color: period.active ? 'text.primary' : 'text.disabled',
                      fontStyle: period.active ? 'normal' : 'italic'
                    }}
                  >
                    {period.name} {period.active ? '' : '(Inactive)'}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" size="small" onClick={() => handleOpenEdit(period)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  edge="end"
                  size="small"
                  sx={{ color: 'error.main' }}
                  onClick={() => confirmDeletePeriod(period)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingPeriod ? `Edit ${editingPeriod.period}` : 'Add Period'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Name field using Stack + Typography style */}
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Typography variant="body1" sx={{ flexShrink: 0, width: '80px' }}>Name:</Typography>
              <TextField
                value={formValues.name}
                size="small"
                fullWidth
                onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                inputProps={{ maxLength: 20 }}
              />
              <FormHelperText>{(formValues.name || '').length}/20</FormHelperText>
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formValues.active}
                  onChange={(e) => setFormValues({ ...formValues, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving} size="small">Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete period{' '}
              <strong>{periodToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} size="small">Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteConfirmed} size="small" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
    </>
  )
}